import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { environment } from '../../environments/environment';
import { OfferIface } from '../interfaces/offer.iface';
import { HttpClient, HttpParams } from '@angular/common/http';
import { OffertTemplateIface } from '../interfaces/offertTemplate.iface';
import { Count } from '../interfaces/count.interface';
import { CRUD } from '../interfaces/crud.interface';
import { OffersMassEditionService } from './offers-mass-edition.service';
import { SelectedOffer } from '../interfaces/selected-offer.model';
import { Tag } from '../interfaces/tag';
import { ToastsManager } from 'ng2-toastr';
import { LoggerService } from './logger.service';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { OffersValidCsvIface } from '../interfaces/offersValidCsv.iface';
import { CommentIface } from '../interfaces/comment.iface';
import { CustomEncoder } from '../helpers/params-encoder';
import { TranslateService } from '@ngx-translate/core';
import { MassOffersEditParamsIface } from '../interfaces/massOffersEditParams.iface';

@Injectable()
export class OffersService implements CRUD<OfferIface> {
  public csvValidationInfo$ = new BehaviorSubject<OffersValidCsvIface[]>([]);
  public currentErrors = this.csvValidationInfo$.asObservable();
  public comment$ = new BehaviorSubject<CommentIface>(null);
  public currentStatus$ = new BehaviorSubject<string>(null);
  private url: string;
  private offerTemplate: OffertTemplateIface;

  constructor(
    private http: HttpClient,
    private offersMassEditionService: OffersMassEditionService,
    private toastr: ToastsManager,
    public translate: TranslateService
  ) {
    this.url = `${environment.OFFERS_API_URL}`;
  }

  findAll(query: any, pageIndex: number, pageSize: number): Observable<OfferIface[]> {
    const params = new HttpParams({ encoder: new CustomEncoder() })
      .set('filter', JSON.stringify(this.encodeQueryTags(query)))
      .set('pageIndex', pageIndex.toString())
      .set('pageSize', pageSize.toString());
    return this.http.get<OfferIface[]>(this.url, { params });
  }

  findAllMatchingIds(query: any): void {
    const queryParams = new HttpParams({ encoder: new CustomEncoder() }).set(
      'filter',
      JSON.stringify(this.encodeQueryTags(query))
    );

    this.http
      .get<OfferIface[]>(this.url, { params: queryParams })
      .map(offers => {
        this.offersMassEditionService.taggedOffers = offers;
        return offers.map(offer => {
          return new SelectedOffer(offer._id, true);
        });
      })
      .subscribe(
        matchingOffers => {
          this.offersMassEditionService.selectedOffers = matchingOffers;
        },
        error => {
          LoggerService.log('OffersService::findAllMatchingIds', error);
          this.toastr.error(this.translate.instant('SERVICE.ERROR'));
        }
      );
  }

  findById(id: string): Observable<OfferIface> {
    return this.http.get<OfferIface>(`${this.url}/${id}`);
  }

  findTags(query: object): Observable<Tag[]> {
    const queryParams = new HttpParams({ encoder: new CustomEncoder() }).set(
      'filter',
      JSON.stringify(this.encodeQueryTags(query))
    );
    return this.http.get<Tag[]>(`${this.url}/tags`, { params: queryParams });
  }

  count(query: any): Observable<Count> {
    const queryParams = new HttpParams({ encoder: new CustomEncoder() }).set(
      'filter',
      JSON.stringify(this.encodeQueryTags(query))
    );
    return this.http.get<Count>(`${this.url}/count`, { params: queryParams });
  }

  create(offer: OfferIface): Observable<OfferIface> {
    return this.http.post<OfferIface>(this.url, offer);
  }

  update(id: string, offer: OfferIface): Observable<OfferIface> {
    return this.http.put<OfferIface>(`${this.url}/${id}`, offer);
  }

  remove(id: string): Observable<OfferIface> {
    return this.http.delete<OfferIface>(`${this.url}/${id}`);
  }

  withdraw(query: object): Observable<OfferIface[]> {
    const queryParams = new HttpParams().set('filter', JSON.stringify(query));
    return this.http.put<OfferIface[]>(`${this.url}/withdraw`, {}, { params: queryParams });
  }

  massUpdate(query: any, apiName: string): Observable<OfferIface[]> {
    const queryParams = new HttpParams().set('filter', JSON.stringify(query));
    return this.http.put<OfferIface[]>(`${this.url}/${apiName}`, {}, { params: queryParams });
  }

  massOffersUpdate(query: MassOffersEditParamsIface): Observable<OfferIface[]> {
    const queryParams = new HttpParams().set('filter', JSON.stringify(query));
    return this.http.put<OfferIface[]>(`${this.url}/updateOffers`, {}, { params: queryParams });
  }

  downloadCSV(query: any = '') {
    let options = {};
    if (query) {
      options = new HttpParams({ encoder: new CustomEncoder() }).set(
        'filter',
        JSON.stringify(this.encodeQueryTags(query))
      );
    }
    return this.http.get(`${this.url}/csv`, { params: options, responseType: 'text' });
  }

  get getOfferTemplate(): OffertTemplateIface {
    return this.offerTemplate;
  }

  set setOfferTemplate(template: OffertTemplateIface) {
    this.offerTemplate = template;
  }

  private encodeQueryTags(query: object): object {
    const encodedQuery = JSON.parse(JSON.stringify(query));
    if (encodedQuery.hasOwnProperty('tags.value')) {
      encodedQuery['tags.value'].$all = encodedQuery['tags.value'].$all.map(el => encodeURIComponent(el));
    }
    return encodedQuery;
  }
}



// WEBPACK FOOTER //
// ./src/app/services/offers.service.ts